import _ from "lodash";
import { Ref } from "vue";

export function replaceAll<T>(array: T[], newElements: T[]) {
    array.splice(0, newElements.length, ...newElements);
}

export function nullSafeSortNumbers(
    a: number | null | undefined,
    b: number | null | undefined
): number {
    return nullSafeSort(a, b, (a, b) => {
        return a - b;
    });
}

export function nullSafeSortStrings(
    a: string | null | undefined,
    b: string | null | undefined
): number {
    return nullSafeSort(a, b, (a, b) => {
        return a.localeCompare(b);
    });
}

export function nullSafeSortDates(a: Date | null | undefined, b: Date | null | undefined): number {
    return nullSafeSort(a, b, (a, b) => {
        return new Date(a).getTime() - new Date(b).getTime();
    });
}

export function nullSafeSort<T>(
    a: T | null | undefined,
    b: T | null | undefined,
    notNullComparatorFn: (a: T, b: T) => number
): number {
    const aNil = _.isNil(a);
    const bNil = _.isNil(b);
    if (aNil && bNil) {
        return 0;
    } else if (aNil) {
        return 1;
    } else if (bNil) {
        return -1;
    } else {
        return notNullComparatorFn(a, b);
    }
}

export function removeFromArrayRef<T>(
    arrayRef: Ref<T[]>,
    item: T,
    finderFn?: (item: T) => boolean
): void {
    arrayRef.value = remove(arrayRef.value, item, finderFn);
}

export function remove<T>(array: T[], item: T, finderFn?: (item: T) => boolean): T[] {
    const existingIndex = finderFn ? array.findIndex(finderFn) : array.indexOf(item);
    if (existingIndex > -1) {
        return array.toSpliced(existingIndex, 1);
    } else {
        return array;
    }
}

export function upsertInArrayRef<T>(
    arrayRef: Ref<T[]>,
    item: T,
    finderFn: (item: T) => boolean
): void {
    const array = arrayRef.value;
    arrayRef.value = upsert(array, item, finderFn);
}

export function mutateUpsert<T>(array: T[], item: T, finderFn: (item: T) => boolean) {
    const updated = upsert(array, item, finderFn);
    replaceAll(array, updated);
}

export function upsert<T>(array: T[], item: T, finderFn: (item: T) => boolean) {
    const existingIndex = array.findIndex(finderFn);
    if (existingIndex > -1) {
        return array.toSpliced(existingIndex, 1, item);
    } else {
        return [...array, item];
    }
}

export function parseToNumberArray(text: string): number[] {
    if (!text) {
        // shouldn't happen since it's a required field
        return [];
    }
    if (text.indexOf(",") === -1) {
        return [Number(text)];
    }
    return text.split(",").map(Number);
}

export function numberArrayToText(numberArray: number[]): string {
    if (!numberArray) {
        return "";
    }
    return numberArray.join(", ");
}

export function permute<T>(arrays: T[][]): T[][] {
    const result: T[][] = [];

    function permuteIntoArray(currentPermutation: T[], arrayIndex: number) {
        // Base case: If we've processed all inner arrays, add the current permutation to the result
        if (arrayIndex === arrays.length) {
            result.push([...currentPermutation]); // Push a copy to avoid mutation issues
            return;
        }

        // Recursive step: Iterate through elements of the current inner array
        for (const item of arrays[arrayIndex]) {
            currentPermutation.push(item); // Add the current item to the permutation
            permuteIntoArray(currentPermutation, arrayIndex + 1); // Recurse for the next inner array
            currentPermutation.pop(); // Backtrack: remove the item to explore other possibilities
        }
    }

    permuteIntoArray([], 0); // Start the backtracking process with an empty permutation and the first inner array
    return result;
}

export function equalIgnoringOrder<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) {
        return false;
    }
    return _.isEqual(_.sortBy(arr1), _.sortBy(arr2));
}
